import React, { useEffect, useState, useCallback } from 'react';

function currentYearMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function PmsExpenseBudget({ token, selectedProperty }) {
  const [yearMonth, setYearMonth] = useState(currentYearMonth());
  const [budgets, setBudgets] = useState([]);
  const [variance, setVariance] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editForm, setEditForm] = useState({ category_id: '', department_id: '', budget_amount: '' });

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const [bRes, vRes, cRes, dRes] = await Promise.all([
        fetch(`/api/v1/expense/budgets?year_month=${yearMonth}`, { headers: headers() }),
        fetch(`/api/v1/expense/budget/variance?year_month=${yearMonth}`, { headers: headers() }),
        fetch('/api/v1/expense/categories', { headers: headers() }),
        fetch('/api/v1/expense/departments', { headers: headers() })
      ]);
      const [bData, vData, cData, dData] = await Promise.all([bRes.json(), vRes.json(), cRes.json(), dRes.json()]);
      if (bRes.ok) setBudgets(bData.rows || []);
      if (vRes.ok) setVariance(vData.rows || []);
      if (cRes.ok) setCategories(cData.rows || []);
      if (dRes.ok) setDepartments(dData.rows || []);
      setError('');
    } catch { setError('Network error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id, yearMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const addBudget = async () => {
    if (!editForm.category_id || !editForm.budget_amount) return setError('Category and amount required');
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/expense/budgets', {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          budgets: [{
            year_month: yearMonth,
            category_id: Number(editForm.category_id),
            department_id: editForm.department_id ? Number(editForm.department_id) : null,
            budget_amount: Number(editForm.budget_amount)
          }]
        })
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage('Budget saved.');
      setEditForm({ category_id: '', department_id: '', budget_amount: '' });
      loadData();
    } catch { setError('Network error'); }
  };

  const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const inputStyle = { padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' };
  const catName = (id) => categories.find(c => c.category_id === id)?.category_name || '—';
  const deptName = (id) => departments.find(d => d.department_id === id)?.dept_name || 'All depts';

  const totalBudget = budgets.reduce((s, b) => s + Number(b.budget_amount || 0), 0);
  const totalActual = variance.reduce((s, v) => s + Number(v.actual_amount || 0), 0);

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Budget Management</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '0.8rem' }}>
        <label style={{ fontSize: '0.85rem' }}>Month:</label>
        <input type="month" value={yearMonth} onChange={e => setYearMonth(e.target.value)} style={inputStyle} />
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#6b7280' }}>
          Total Budget: <strong>{fmtMoney(totalBudget)}</strong> · Actual: <strong>{fmtMoney(totalActual)}</strong>
        </span>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem' }}>
        <h3 style={{ margin: '0 0 0.7rem 0', fontSize: '1rem' }}>Add / Update Budget</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.7rem' }}>
          <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.8rem' }}>
            <span>Category *</span>
            <select value={editForm.category_id} onChange={e => setEditForm(f => ({ ...f, category_id: e.target.value }))} style={inputStyle}>
              <option value="">— Select —</option>
              {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.8rem' }}>
            <span>Department (optional)</span>
            <select value={editForm.department_id} onChange={e => setEditForm(f => ({ ...f, department_id: e.target.value }))} style={inputStyle}>
              <option value="">All</option>
              {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.dept_name}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.8rem' }}>
            <span>Amount *</span>
            <input type="number" step="0.01" value={editForm.budget_amount} onChange={e => setEditForm(f => ({ ...f, budget_amount: e.target.value }))} style={inputStyle} />
          </label>
          <div style={{ alignSelf: 'end' }}>
            <button onClick={addBudget} style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Save</button>
          </div>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '0.6rem 1rem', borderBottom: '1px solid #e5e7eb', background: '#fafafa', fontWeight: 600 }}>Budget vs Actual — {yearMonth}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: '0.55rem' }}>Category</th>
                <th style={{ padding: '0.55rem' }}>Department</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>Budget</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>Actual</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>Variance</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {variance.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No budgets set for {yearMonth}.</td></tr>
              ) : variance.map((v, i) => {
                const over = Number(v.variance_pct || 0) > 0;
                return (
                  <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.5rem' }}>{catName(v.category_id)}</td>
                    <td style={{ padding: '0.5rem' }}>{deptName(v.department_id)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtMoney(v.budget_amount)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtMoney(v.actual_amount)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: over ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{fmtMoney(v.variance)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: over ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{v.variance_pct == null ? '—' : `${Number(v.variance_pct).toFixed(1)}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PmsExpenseBudget;
